import { ToastController } from '@ionic/angular';
import { LiveUpdate } from '@capawesome/capacitor-live-update';

/**
 * Tell the user an OTA bundle has been staged, without interrupting them.
 *
 * Both OTA paths need this and neither owns it: AppComponent's foreground check
 * and UserService's release-track sync. It is a standalone function taking the
 * ToastController rather than a service so neither of those gains a dependency
 * on the other.
 *
 * Non-blocking by design. Both paths previously used a native confirm(), which
 * freezes the WebView — every JS timer stops and then fires at once on
 * dismissal, which could trip the calendar's own load watchdog into reporting a
 * failure that never happened. Answering yes also reloaded immediately,
 * discarding whatever was on screen; on UserService's path that could be a
 * half-built booking or an open checkout, since it runs on a 15-minute timer.
 *
 * Nothing is lost by staying quiet: sync() has already downloaded and staged
 * the bundle, and ready() applies it on the next app start regardless. "Restart
 * now" is opt-in, so a reload can only discard work the user chose to discard.
 */
export async function presentLiveUpdateToast(
  toastCtrl: ToastController
): Promise<void> {
  const toast = await toastCtrl.create({
    message: 'Update downloaded — it will apply next time you reopen the app.',
    duration: 8000,
    position: 'bottom',
    buttons: [
      {
        text: 'Restart now',
        handler: () => {
          void LiveUpdate.reload();
        },
      },
    ],
  });
  await toast.present();
}
